/**
 * File: numbersUtils.ts
 * Path: apps/admin/src/shared/utils/numbersUtils.ts
 * Description: The numbers behind the Numbers page, worked out from the
 * records already in the CRM. Pure: the page loads the records, this adds
 * them up for one period, and every number carries the list of records it
 * came from so it can be opened. Money is whole cents.
 *
 * A number with nothing to go on is null (the page says "Not enough yet"),
 * never zero: a zero win rate and "no deals decided" are different things.
 */

import { contractOwedPayments, southAfricanDay, type PaymentDueDate, formatCents } from "@idk/config";
import { computeContractSchedule } from "@idk/config";
import { randToCents } from "@idk/config";
import type { PaymentReceipt } from "@idk/config";
import type { Contract } from "@idk/config";
import type { Client } from "@idk/config";
import type { CostEntry, MonthlyCosts, TimeEntry } from "@idk/config";
import { sharedCostTotals } from "@idk/config";
import { LEAD_SOURCE_LABELS, type Lead, type LeadSource } from "@idk/config";
import { ADMIN_PATHS } from "@idk/config";
import { retainerStats, type Retainer } from "@idk/config";
import { dayInPeriod, msInPeriod, monthsOf, type Period } from "./numbersPeriodUtils";
import type { NumberId } from "./numbersDefinitionsData";

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

export type NumberUnit = "count" | "percent" | "cents" | "days" | "hours" | "ratio" | "perDay";

/** One record behind a number. */
export interface DrillRow {
  id: string;
  label: string;
  detail: string;
  /** Where the record lives in the admin, or null. */
  href: string | null;
}

export interface NumberItem {
  id: NumberId;
  value: number | null;
  unit: NumberUnit;
  rows: DrillRow[];
}

export interface ConversionStep {
  key: "contacted" | "talking" | "quoted" | "won";
  label: string;
  count: number;
  /** Of the leads added in the period; null when none were added. */
  percent: number | null;
  rows: DrillRow[];
}

export interface SourceRow {
  source: LeadSource | "none";
  label: string;
  leads: number;
  wins: number;
  /** Wins of the leads decided in the period; null when none. */
  winRate: number | null;
  wonCents: number;
  effortCents: number;
  costPerWinCents: number | null;
  rows: DrillRow[];
}

export interface PaymentsOwed {
  lateCents: number;
  comingCents: number;
  undatedCents: number;
  totalCents: number;
}

export interface NumbersInput {
  today: string;
  leads: readonly Lead[];
  clients: readonly Client[];
  contracts: readonly Contract[];
  receipts: Readonly<Record<string, readonly PaymentReceipt[]>>;
  dueDates: Readonly<Record<string, readonly PaymentDueDate[]>>;
  time: readonly TimeEntry[];
  costs: readonly CostEntry[];
  monthly: readonly MonthlyCosts[];
  /** Merged retainers (mergeRetainers). */
  retainers: readonly Retainer[];
  /** Calls, WhatsApps and emails logged since the period began; null when unknown. */
  outreach: number | null;
  now: number;
}

export interface NumbersSnapshot {
  period: Period;
  sales: {
    leads: NumberItem;
    winRate: NumberItem;
    conversion: ConversionStep[];
    dealSize: NumberItem;
    salesCycle: NumberItem;
    speedToLead: NumberItem;
    outreach: NumberItem;
  };
  sources: SourceRow[];
  /** `roas` is null until ad spend has been entered for the period. */
  value: { cac: NumberItem; ltv: NumberItem; ltvCac: NumberItem; cash30: NumberItem; roas: NumberItem | null };
  recurring: { mrr: NumberItem; churn: NumberItem; carePlan: NumberItem };
  cash: { owed: NumberItem; owedSplit: PaymentsOwed; daysToPay: NumberItem; collected: NumberItem };
}

const rand = (cents: number): string => formatCents(cents);

const mean = (values: readonly number[]): number | null =>
  values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;

const leadRow = (lead: Lead, detail: string): DrillRow => ({
  id: lead.id,
  label: lead.name,
  detail,
  href: ADMIN_PATHS.businesses.detail(lead.id),
});

const signed = (c: Contract): boolean => c.status === "client_signed" || c.status === "signed";

const signedMs = (c: Contract): number | null => c.clientSignature?.signedAt ?? c.signedAt;

const dayNumber = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;

/** The value a signed contract is worth: its schedule's total (a retainer's minimum term). */
const contractValueCents = (c: Contract): number => randToCents(computeContractSchedule(c.content).total);

export const buildNumbers = (input: NumbersInput, period: Period): NumbersSnapshot => {
  const { leads, clients, contracts } = input;
  const inP = (ms: number | null) => msInPeriod(ms, period);

  // ─── Sales ──────────────────────────────────────────────────────────
  const added = leads.filter((l) => inP(l.createdAt));
  const wonLeads = leads.filter((l) => inP(l.wonAt));
  const lostLeads = leads.filter((l) => inP(l.lostAt));
  const decided = wonLeads.length + lostLeads.length;

  const winRate: NumberItem = {
    id: "win-rate",
    value: decided === 0 ? null : (wonLeads.length / decided) * 100,
    unit: "percent",
    rows: [
      ...wonLeads.map((l) => leadRow(l, "Won")),
      ...lostLeads.map((l) => leadRow(l, l.lostReason ? `Lost: ${l.lostReason}` : "Lost")),
    ],
  };

  const reached = (key: "contactedAt" | "talkingAt" | "quotedAt" | "wonAt") => added.filter((l) => l[key] !== null);
  const step = (key: ConversionStep["key"], label: string, field: "contactedAt" | "talkingAt" | "quotedAt" | "wonAt"): ConversionStep => {
    const list = reached(field);
    return {
      key,
      label,
      count: list.length,
      percent: added.length === 0 ? null : (list.length / added.length) * 100,
      rows: list.map((l) => leadRow(l, label)),
    };
  };

  const withValue = wonLeads.filter((l) => l.value !== null && l.value > 0);
  const dealSize: NumberItem = {
    id: "deal-size",
    value: withValue.length === 0 ? null : Math.round(mean(withValue.map((l) => randToCents(l.value as number))) as number),
    unit: "cents",
    rows: withValue.map((l) => leadRow(l, rand(randToCents(l.value as number)))),
  };

  const cycled = wonLeads.filter((l) => l.createdAt !== null && l.wonAt !== null);
  const salesCycle: NumberItem = {
    id: "sales-cycle",
    value: mean(cycled.map((l) => Math.max(0, ((l.wonAt as number) - (l.createdAt as number)) / DAY_MS))),
    unit: "days",
    rows: cycled.map((l) => leadRow(l, `${Math.round(Math.max(0, ((l.wonAt as number) - (l.createdAt as number)) / DAY_MS))} days`)),
  };

  const touched = added.filter((l) => l.createdAt !== null && l.contactedAt !== null);
  const speed: NumberItem = {
    id: "speed-to-lead",
    value: mean(touched.map((l) => Math.max(0, ((l.contactedAt as number) - (l.createdAt as number)) / HOUR_MS))),
    unit: "hours",
    rows: touched.map((l) => leadRow(l, `${(Math.max(0, ((l.contactedAt as number) - (l.createdAt as number)) / HOUR_MS)).toFixed(1)} hours`)),
  };

  const outreach: NumberItem = {
    id: "outreach",
    value: input.outreach === null || period.elapsedDays === 0 ? null : input.outreach / period.elapsedDays,
    unit: "perDay",
    rows: [],
  };

  // ─── Cost to win ────────────────────────────────────────────────────
  const time = input.time.filter((t) => dayInPeriod(t.date, period));
  const costs = input.costs.filter((c) => dayInPeriod(c.date, period));
  const months = new Set(monthsOf(period));
  const shared = input.monthly.filter((m) => months.has(m.month)).map(sharedCostTotals);
  const timeCents = time.reduce((s, t) => s + t.costCents, 0);
  const costCents = costs.reduce((s, c) => s + c.amountCents, 0);
  const winningCents = shared.reduce((s, t) => s + t.winning, 0);
  const adCents = shared.reduce((s, t) => s + t.adSpend, 0);
  const spendCents = timeCents + costCents + winningCents + adCents;

  const cac: NumberItem = {
    id: "cac",
    value: wonLeads.length === 0 ? null : Math.round(spendCents / wonLeads.length),
    unit: "cents",
    rows: [
      { id: "time", label: "Your time at your rates", detail: rand(timeCents), href: null },
      { id: "costs", label: "Costs logged on leads and clients", detail: rand(costCents), href: null },
      { id: "winning", label: "Monthly winning-clients costs", detail: rand(winningCents), href: null },
      { id: "ads", label: "Ad spend", detail: rand(adCents), href: null },
      ...wonLeads.map((l) => leadRow(l, "Won in this period")),
    ],
  };

  // ─── Lifetime value ─────────────────────────────────────────────────
  const perClient = new Map<string, { cents: number; name: string }>();
  for (const c of contracts.filter((x) => signed(x) && x.clientId)) {
    const entry = perClient.get(c.clientId as string) ?? { cents: 0, name: c.content.client.name };
    entry.cents += contractValueCents(c);
    perClient.set(c.clientId as string, entry);
  }
  const ltvValue = mean([...perClient.values()].map((v) => v.cents));
  const ltv: NumberItem = {
    id: "ltv",
    value: ltvValue === null ? null : Math.round(ltvValue),
    unit: "cents",
    rows: [...perClient.entries()].map(([id, v]) => ({ id, label: v.name, detail: rand(v.cents), href: ADMIN_PATHS.businesses.detail(id) })),
  };
  const ltvCac: NumberItem = {
    id: "ltv-cac",
    value: ltv.value !== null && cac.value !== null && cac.value > 0 ? ltv.value / cac.value : null,
    unit: "ratio",
    rows: [],
  };

  // 30-day cash per new client: money received within 30 days of the first signing.
  const firstSigned = new Map<string, number>();
  for (const c of contracts.filter((x) => signed(x) && x.clientId && signedMs(x) !== null)) {
    const id = c.clientId as string;
    const ms = signedMs(c) as number;
    if (!firstSigned.has(id) || ms < (firstSigned.get(id) as number)) firstSigned.set(id, ms);
  }
  const newClients = [...firstSigned.entries()].filter(([, ms]) => inP(ms));
  const cashByClient = newClients.map(([id, ms]) => {
    const limit = southAfricanDay(ms + 30 * DAY_MS);
    const cents = contracts
      .filter((c) => c.clientId === id && signed(c))
      .flatMap((c) => input.receipts[c.id] ?? [])
      .filter((r) => r.receivedOn <= limit)
      .reduce((s, r) => s + randToCents(r.amount), 0);
    return { id, cents, name: perClient.get(id)?.name ?? id };
  });
  const cash30: NumberItem = {
    id: "cash-30",
    value: mean(cashByClient.map((c) => c.cents)),
    unit: "cents",
    rows: cashByClient.map((c) => ({ id: c.id, label: c.name, detail: rand(c.cents), href: ADMIN_PATHS.businesses.detail(c.id) })),
  };

  // ─── Recurring ──────────────────────────────────────────────────────
  const stats = retainerStats(input.retainers, contracts as Contract[], input.now);
  const active = input.retainers.filter((r) => r.status === "active");
  const cancelledIn = input.retainers.filter((r) => r.status === "cancelled" && r.endDate !== null && dayInPeriod(r.endDate, period));
  const churnBase = active.length + cancelledIn.length;
  const retainerRow = (r: Retainer, detail: string): DrillRow => ({
    id: r.id,
    label: r.clientName,
    detail,
    href: r.clientId ? ADMIN_PATHS.businesses.detail(r.clientId) : ADMIN_PATHS.carePlans.root,
  });
  const mrr: NumberItem = {
    id: "mrr",
    value: stats.mrrCents,
    unit: "cents",
    rows: active.map((r) => retainerRow(r, `${rand(r.monthlyCents)} a month`)),
  };
  const churn: NumberItem = {
    id: "churn",
    value: churnBase === 0 ? null : (cancelledIn.length / churnBase) * 100,
    unit: "percent",
    rows: cancelledIn.map((r) => retainerRow(r, `Cancelled ${r.endDate}`)),
  };
  const carePlan: NumberItem = {
    id: "care-plan",
    value: stats.websiteClients === 0 ? null : (stats.withCarePlan / stats.websiteClients) * 100,
    unit: "percent",
    rows: [],
  };

  // ─── Cash ───────────────────────────────────────────────────────────
  const owedRows = contracts.flatMap((c) => contractOwedPayments(c, input.receipts[c.id] ?? [], input.dueDates[c.id] ?? [], input.today));
  const owedSplit: PaymentsOwed = {
    lateCents: owedRows.filter((p) => p.dueOn !== null && p.dueOn < input.today).reduce((s, p) => s + p.owedCents, 0),
    comingCents: owedRows.filter((p) => p.dueOn !== null && p.dueOn >= input.today).reduce((s, p) => s + p.owedCents, 0),
    undatedCents: owedRows.filter((p) => p.dueOn === null).reduce((s, p) => s + p.owedCents, 0),
    totalCents: owedRows.reduce((s, p) => s + p.owedCents, 0),
  };
  const owed: NumberItem = {
    id: "owed",
    value: owedSplit.totalCents,
    unit: "cents",
    rows: owedRows.map((p) => ({
      id: p.reference,
      label: p.clientName,
      detail: `${p.label}, ${rand(p.owedCents)}${p.dueOn ? `, due ${p.dueOn}` : ", no due date yet"}`,
      href: ADMIN_PATHS.contracts.detail(p.contractId),
    })),
  };

  const received = contracts
    .filter(signed)
    .flatMap((c) => (input.receipts[c.id] ?? []).map((r) => ({ c, r })))
    .filter(({ r }) => dayInPeriod(r.receivedOn, period));
  const collected: NumberItem = {
    id: "collected",
    value: received.reduce((s, { r }) => s + randToCents(r.amount), 0),
    unit: "cents",
    rows: received.map(({ c, r }) => ({
      id: r.reference,
      label: c.content.client.name,
      detail: `${rand(randToCents(r.amount))} on ${r.receivedOn}`,
      href: ADMIN_PATHS.contracts.detail(c.id),
    })),
  };

  const lateDays = received.flatMap(({ c, r }) => {
    const due = (input.dueDates[c.id] ?? []).find((d) => d.reference === r.reference)?.dueOn;
    return due ? [{ c, r, days: Math.max(0, dayNumber(r.receivedOn) - dayNumber(due)) }] : [];
  });
  const daysToPay: NumberItem = {
    id: "days-to-pay",
    value: mean(lateDays.map((x) => x.days)),
    unit: "days",
    rows: lateDays.map(({ c, r, days }) => ({
      id: r.reference,
      label: c.content.client.name,
      detail: `${days} days after the due date`,
      href: ADMIN_PATHS.contracts.detail(c.id),
    })),
  };

  // ─── Sources ────────────────────────────────────────────────────────
  const sourceOf = (lead: Lead | undefined): LeadSource | "none" => lead?.source ?? "none";
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const clientLead = new Map(clients.map((c) => [c.id, c.leadId ?? c.id]));
  const effortFor = new Map<string, number>();
  const addEffort = (kind: string, id: string, cents: number) => {
    const leadId = kind === "client" ? (clientLead.get(id) ?? id) : id;
    effortFor.set(leadId, (effortFor.get(leadId) ?? 0) + cents);
  };
  for (const t of time) addEffort(t.recordKind, t.recordId, t.costCents);
  for (const c of costs) addEffort(c.recordKind, c.recordId, c.amountCents);

  const keys = new Set<LeadSource | "none">([...leads.map((l) => l.source)]);
  for (const id of effortFor.keys()) keys.add(sourceOf(leadById.get(id)));
  const sources: SourceRow[] = [...keys]
    .map((source) => {
      const mine = (l: Lead) => l.source === source;
      const sAdded = added.filter(mine);
      const sWon = wonLeads.filter(mine);
      const sLost = lostLeads.filter(mine);
      const effortCents = [...effortFor.entries()].filter(([id]) => sourceOf(leadById.get(id)) === source).reduce((s, [, c]) => s + c, 0);
      const sDecided = sWon.length + sLost.length;
      return {
        source,
        label: source === "none" ? "Not linked to a lead" : LEAD_SOURCE_LABELS[source],
        leads: sAdded.length,
        wins: sWon.length,
        winRate: sDecided === 0 ? null : (sWon.length / sDecided) * 100,
        wonCents: sWon.reduce((s, l) => s + (l.value ? randToCents(l.value) : 0), 0),
        effortCents,
        costPerWinCents: sWon.length === 0 ? null : Math.round(effortCents / sWon.length),
        rows: [...sAdded.map((l) => leadRow(l, "Added")), ...sWon.map((l) => leadRow(l, "Won"))],
      };
    })
    .filter((r) => r.leads > 0 || r.wins > 0 || r.effortCents > 0)
    .sort((a, b) => b.wonCents - a.wonCents || b.leads - a.leads);

  return {
    period,
    sales: {
      leads: { id: "leads", value: added.length, unit: "count", rows: added.map((l) => leadRow(l, LEAD_SOURCE_LABELS[l.source])) },
      winRate,
      conversion: [
        step("contacted", "Contacted", "contactedAt"),
        step("talking", "Talking", "talkingAt"),
        step("quoted", "Quote sent", "quotedAt"),
        step("won", "Won", "wonAt"),
      ],
      dealSize,
      salesCycle,
      speedToLead: speed,
      outreach,
    },
    sources,
    value: {
      cac,
      ltv,
      ltvCac,
      cash30,
      roas:
        adCents > 0
          ? {
              id: "roas",
              value: wonLeads.reduce((sum, l) => sum + (l.value ? randToCents(l.value) : 0), 0) / adCents,
              unit: "ratio",
              rows: wonLeads.map((l) => leadRow(l, "Won in this period")),
            }
          : null,
    },
    recurring: { mrr, churn, carePlan },
    cash: { owed, owedSplit, daysToPay, collected },
  };
};
